import React, { useCallback, useEffect, useMemo, useState } from "react";

export const FakeContext = React.createContext({});

const defaultJoinCallback = (assistance) => {
  console.log("Join performed", assistance);
};

const defaultLeaveCallback = (assistance) => {
  console.log("Leave performed", assistance);
};

const defaultMagicPasteCallback = (fields) => {
  console.log("Magic paste performed", fields);
};

/**
 * The fake context provider
 * 
 * @param {Object} props - The props of the component
 * @returns The fake context provider
 */
const FakeProvider = ({ children, isRippleEnabled, setIsRippleEnabled }) => {
  const [assistanceHistory, setAssistanceHistory] = useState(
    JSON.parse(localStorage.getItem("ta_assistance_history")) || [],
  );
  const [joinCallback, setJoinCallback] = useState(() => defaultJoinCallback);
  const [leaveCallback, setLeaveCallback] = useState(() => defaultLeaveCallback);
  const [magicPasteCallback, setMagicPasteCallback] = useState(
    () => defaultMagicPasteCallback,
  );
  const [isCalling, setIsCalling] = useState(false);

  /**
   * Adds an assistance to the history
   * 
   * @param {Object} assistance - The assistance to add
   */
  const addAssistanceToHistory = useCallback((assistance) => {
    setAssistanceHistory((prevState) => [...prevState, assistance]);
  }, []);

  /**
   * Gets an assistance from the history
   * 
   * @param {string} assistanceRequestId - The assistance request id
   * @returns The assistance
   */
  const getAssistanceFromHistory = useCallback(
    (assistanceRequestId) => {
      return assistanceHistory.find((item) => item.id === assistanceRequestId);
    },
    [assistanceHistory],
  );

  /**
   * Updates an assistance in the history
   * 
   * @param {Object} assistance - The assistance to update
   */
  const updateAssistanceFromHistory = useCallback((assistance) => {
    setAssistanceHistory((prevState) =>
      prevState.map((item) => (item.id === assistance.id ? assistance : item)),
    );
  }, []);

  /**
   * Removes an assistance from the history
   * 
   * @param {string} assistanceRequestId - The assistance request id
   */
  const removeAssistanceFromHistory = useCallback((assistanceRequestId) => {
    setAssistanceHistory((prevState) =>
      prevState.filter((item) => item.id !== assistanceRequestId),
    );
  }, []);

  /**
   * Removes all assistances from the history
   */
  const removeAllAssistanceFromHistory = useCallback(() => {
    setAssistanceHistory([]);
  }, []);

  /**
   * Handles the call start event
   * 
   * @param {Object} assistance - The assistance
   */
  const onCallStart = useCallback(
    (assistance) => {
      const existingAssistance = getAssistanceFromHistory(
        assistance.assistance_request_id,
      );
      const driverNames = assistance?.driver_name.split(" ");
      const newAssistance = {
        beneficiaryName: assistance?.driver_name,
        licensePlate: assistance?.license_plate,
        firstName: driverNames[0],
        lastName: driverNames.slice(1).join(" "),
        phoneNumber: assistance?.driver_phone_number,
        serialNumber: assistance?.VIN,
        status: "open",
        timestamp: Date.now(),
      };

      if (existingAssistance) {
        updateAssistanceFromHistory({
          ...existingAssistance,
          ...newAssistance,
        });
      } else {
        addAssistanceToHistory({
          id: assistance.assistance_request_id,
          ...newAssistance,
        });
      }
      setIsCalling(true);
      joinCallback(assistance);
    },
    [
      addAssistanceToHistory,
      getAssistanceFromHistory,
      joinCallback,
      updateAssistanceFromHistory,
    ],
  );

  /**
   * Handles the call end event
   */
  const onCallEnd = useCallback(() => {
    setIsCalling(false);
    leaveCallback();
  }, [leaveCallback]);

  /**
   * Handles the magic paste event
   * 
   * @param {Object} vehicle - The vehicle
   * @param {Object} location - The location
   * @param {Object} assistance - The assistance
   */
  const onMagicPaste = useCallback(
    (vehicle, location, assistance) => {
      magicPasteCallback(vehicle, location, assistance);
    },
    [magicPasteCallback],
  );

  useEffect(() => {
    localStorage.setItem(
      "ta_assistance_history",
      JSON.stringify(assistanceHistory),
    );
  }, [assistanceHistory]);

  /**
   * Toggles the ripple effect
   */
  const toggleRipple = useCallback(() => {
    setIsRippleEnabled(!isRippleEnabled);
  }, [isRippleEnabled, setIsRippleEnabled]);

  const contextValue = useMemo(() => {
    return {
      addAssistanceToHistory,
      assistanceHistory,
      getAssistanceFromHistory,
      isCalling,
      onCallEnd,
      onCallStart,
      onMagicPaste,
      removeAllAssistanceFromHistory,
      removeAssistanceFromHistory,
      setJoinCallback,
      setLeaveCallback,
      setMagicPasteCallback,
      updateAssistanceFromHistory,
      toggleRipple,
      isRippleEnabled,
    };
  }, [
    addAssistanceToHistory,
    assistanceHistory,
    getAssistanceFromHistory,
    isCalling,
    onCallEnd,
    onCallStart,
    onMagicPaste,
    removeAllAssistanceFromHistory,
    removeAssistanceFromHistory,
    setJoinCallback,
    setLeaveCallback,
    setMagicPasteCallback,
    updateAssistanceFromHistory,
    toggleRipple,
    isRippleEnabled,
  ]);

  return (
    <FakeContext.Provider value={contextValue}>{children}</FakeContext.Provider>
  );
};

export default React.memo(FakeProvider);
